import { useState, useEffect } from "react";
import { FaSpinner, FaTimes, FaInfoCircle } from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import { useOrganization } from "../../../context/OrganizationContext";
import { sanitizeEmail } from "../../../utils/sanitization";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../UI/DesignStandards";

const InviteMemberModal = ({ isOpen, onClose, onInviteSent }) => {
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [expirationDays, setExpirationDays] = useState(7);

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setExpirationDays(7);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      // Inline validation below input; no toast
      return;
    }

    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        // Inline banner could be added by parent; no toast
        return;
      }

      // Get the Tournament Official role ID
      const { data: tournamentOfficialRole, error: roleError } = await supabase
        .from("organizationRoles")
        .select("id")
        .eq("name", "Tournament Official")
        .single();

      if (roleError || !tournamentOfficialRole) {
        // Inline error state could be shown; no toast
        return;
      }

      // Check if the email is already a member
      const { data: existingMember, error: checkError } = await supabase.rpc(
        "check_email_membership",
        {
          org_id: currentOrganization.id,
          email_address: email.trim().toLowerCase(),
        }
      );

      if (checkError) throw checkError;

      if (existingMember) {
        // Inline error state could be shown; no toast
        return;
      }

      // Check for existing pending invitations
      const { data: existingInvite, error: inviteCheckError } = await supabase
        .from("organizationInvites")
        .select("id, status")
        .eq("organizationID", currentOrganization.id)
        .eq("email", email.trim().toLowerCase())
        .eq("status", "pending")
        .maybeSingle();

      if (inviteCheckError && inviteCheckError.code !== "PGRST116") {
        throw inviteCheckError;
      }

      if (existingInvite) {
        // Inline error state could be shown; no toast
        return;
      }

      // Calculate expiration date
      const expiredAt = new Date();
      expiredAt.setDate(expiredAt.getDate() + expirationDays);

      // Create the invitation with Tournament Official role
      const { data: invite, error: inviteError } = await supabase
        .from("organizationInvites")
        .insert({
          organizationID: currentOrganization.id,
          email: email.trim().toLowerCase(),
          status: "pending",
          expiredAt: expiredAt.toISOString(),
          invitedBy: user.id,
          roleIDs: [tournamentOfficialRole.id],
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      // Don't generate or store the invitation link
      // The link is only accessible via email for security

      // Send invitation email
      try {
        const { error: emailError } = await supabase.functions.invoke(
          "send-organization-invite-email",
          {
            body: {
              inviteId: invite.id,
              organizationId: currentOrganization.id,
              inviterId: user.id,
              inviteEmail: email.trim().toLowerCase(),
              roleNames: ["Tournament Official"], // Default role for now
            },
          }
        );

        if (emailError) {
          console.error("Error sending invite email:", emailError);
          // Don't fail the whole process if email fails
          notify.warning(
            `Invitation created but email failed to send: ${emailError.message}`
          );
        } else {
          notify.success(`Invitation sent to ${email}`);
        }
      } catch (emailError) {
        console.error("Error sending invite email:", emailError);
        notify.warning(`Invitation created but email failed to send`);
      }

      onInviteSent();
      onClose(); // Close the modal after successful invite
    } catch (error) {
      console.error("Error sending invitation:", error);

      // Handle specific error cases
      if (error.code === "23505") {
        notify.error(
          "An invitation has already been sent to this email address"
        );
      } else {
        notify.error("Failed to send invitation. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-brand-charcoal/60 flex items-center justify-center p-4 z-50">
      <div className="card-board max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-display text-xl">Invite New Member</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-brand-muted hover:text-brand-charcoal"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(sanitizeEmail(e.target.value))}
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="Enter email address"
                  required
                />
              </div>

              {/* Expiration Days */}
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Invitation Expires In
                </label>
                <select
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              {/* Role Information */}
              <div className="card-inset p-4">
                <div className="flex items-start">
                  <FaInfoCircle className="text-brand-muted mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-brand-charcoal mb-2">
                      Default Role Assignment
                    </h4>
                    <p className="text-sm text-brand-muted mb-3">
                      New members will automatically receive the{" "}
                      <strong>Tournament Official</strong> role, which allows
                      them to:
                    </p>
                    <ul className="text-sm text-brand-muted space-y-1">
                      <li className="flex items-center">
                        <div className="w-1 h-1 bg-brand-muted rounded-full mr-2"></div>
                        Enter and edit scores
                      </li>
                      <li className="flex items-center">
                        <div className="w-1 h-1 bg-brand-muted rounded-full mr-2"></div>
                        View tournament information
                      </li>
                      <li className="flex items-center">
                        <div className="w-1 h-1 bg-brand-muted rounded-full mr-2"></div>
                        Participate in events
                      </li>
                    </ul>
                    <p className="text-sm text-brand-muted mt-3">
                      <strong>Note:</strong> Higher-level roles (Admin, Owner)
                      can be assigned after the member joins using the member
                      management tools.
                    </p>
                    <p className="text-sm text-brand-muted mt-2">
                      <strong>New:</strong> Users without accounts can now
                      create an account directly from the invitation link for a
                      streamlined onboarding experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={loading}
                className="!px-3 !py-2 text-sm"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin h-4 w-4 mr-2" />
                    Sending...
                  </div>
                ) : (
                  "Send Invitation"
                )}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
